/* --- START: src/components/CurriculumConfigModal.js --- */
import React, { useState, useEffect, useMemo } from 'react';
import './CurriculumConfigModal.css';

const DAYS_OF_WEEK_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAYS_OF_WEEK_LABELS = ['S', 'M', 'T', 'W', 'R', 'F', 'S'];
const MBE_SUBJECTS = ['torts', 'contracts', 'property', 'civpro', 'evidence', 'criminal', 'constitution'];

const ARCHETYPES = [
    { 
        id: 'traditional', 
        icon: 'menu_book', 
        title: 'Traditional', 
        tag: 'Linear Focus',
        specs: [
            { label: 'Mix-mode', val: 'Off' },
            { label: 'Subjects', val: 'All' },
            { label: 'HFR Filter', val: 'None' },
            { label: 'Logic', val: 'Sequential' }
        ]
    },
    { 
        id: 'interleaved', 
        icon: 'shuffle', 
        title: 'Interleaved', 
        tag: 'Max Retention',
        specs: [
            { label: 'Mix-mode', val: 'On' },
            { label: 'Subjects', val: 'All' },
            { label: 'HFR Filter', val: 'None' },
            { label: 'Logic', val: 'Spaced Weave' }
        ]
    },
    { 
        id: 'high_yield', 
        icon: 'track_changes', 
        title: 'High-Yield', 
        tag: 'Efficiency',
        specs: [
            { label: 'Mix-mode', val: 'On' },
            { label: 'Subjects', val: 'All' },
            { label: 'HFR Filter', val: 'Strict (HFR Only)' },
            { label: 'Logic', val: 'Interleaved' }
        ]
    },
    { 
        id: 'shield', 
        icon: 'shield', 
        title: 'The Shield', 
        tag: 'Retaker Focus',
        specs: [
            { label: 'Mix-mode', val: 'Off' },
            { label: 'Subjects', val: 'MBE Priority' },
            { label: 'HFR Filter', val: 'MEE Only' },
            { label: 'Logic', val: 'Weakness-First' }
        ]
    }
];

const CurriculumConfigModal = ({ isOpen, onClose, subjectMap, userData, allItemsMap, onSaveConfiguration }) => {
    const [subjectSequence, setSubjectSequence] = useState([]);
    const [excludedSubjects, setExcludedSubjects] = useState(new Set());
    const [hfrRestrictions, setHfrRestrictions] = useState(new Set());
    const [deNovoTrackSubjects, setDeNovoTrackSubjects] = useState(new Set());
    const [mixMode, setMixMode] = useState(false);
    const [dailyRuleTarget, setDailyRuleTarget] = useState(3);
    const [targetMasteryDate, setTargetMasteryDate] = useState('');
    const [studyDays, setStudyDays] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [activeArchetype, setActiveArchetype] = useState('custom'); 
    const [draggedItem, setDraggedItem] = useState(null);

    useEffect(() => {
        if (isOpen && userData) {
            const initialSeq = [...new Set([...(userData.diagnostics?.subject_sequence || []), ...Object.keys(subjectMap)])];
            const initialExcl = new Set(userData.goals?.excluded_subjects || []);
            const initialHfr = new Set(userData.goals?.hfr_restricted_subjects || []);
            const initialFast = new Set(userData.goals?.denovo_track_subjects || []);
            const initialMix = userData.goals?.mix_mode || false;
            const initialTarget = userData.goals?.daily_rule_target || 3;
            const initialDate = userData.goals?.target_mastery_date || '';
            const initialDays = userData.goals?.study_days || [];

            setSubjectSequence(initialSeq);
            setExcludedSubjects(initialExcl);
            setHfrRestrictions(initialHfr);
            setDeNovoTrackSubjects(initialFast);
            setMixMode(initialMix);
            setDailyRuleTarget(initialTarget);
            setTargetMasteryDate(initialDate);
            setStudyDays(initialDays);

            setSnapshot({
                subjectSequence: initialSeq, excludedSubjects: initialExcl, hfrRestrictions: initialHfr,
                deNovoTrackSubjects: initialFast, mixMode: initialMix, dailyRuleTarget: initialTarget,
                targetMasteryDate: initialDate, studyDays: initialDays
            });
            setActiveArchetype('custom');
        }
    }, [isOpen, userData, subjectMap]);

    const stats = useMemo(() => {
        if (!allItemsMap) return { inLimine: 0, fastTrack: 0, hfrCount: 0, velocity: 0 };
        let inLimine = 0, fastTrack = 0, hfrCount = 0;

        allItemsMap.forEach(item => {
            if (item.itemType !== 'rule') return;
            if (userData?.rulesProgress?.[item.id]?.srs_data?.lastReviewedDate) return;
            if (excludedSubjects.has(item.subject)) return;

            const isHFR = item.isHFR || item.is_hfr;
            if (hfrRestrictions.has(item.subject) && !isHFR) return;

            if (isHFR) hfrCount++;
            if (deNovoTrackSubjects.has(item.subject)) fastTrack++;
            else inLimine++;
        });

        let validStudyDaysCount = 0;
        if (targetMasteryDate && studyDays.length > 0) {
            const today = new Date(), end = new Date(targetMasteryDate + 'T00:00:00');
            const studyDaysSet = new Set(studyDays.map(d => d.toLowerCase()));
            let iter = new Date(today);
            while (iter <= end) {
                const dayName = iter.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                if (studyDaysSet.has(dayName)) validStudyDaysCount++;
                iter.setDate(iter.getDate() + 1);
            }
        }
        const velocity = validStudyDaysCount > 0 ? Math.ceil((inLimine + fastTrack) / validStudyDaysCount) : 0;
        return { inLimine, fastTrack, hfrCount, velocity };
    }, [allItemsMap, userData, excludedSubjects, hfrRestrictions, deNovoTrackSubjects, targetMasteryDate, studyDays]);

    const isDirty = useMemo(() => {
        if (!snapshot) return false;
        const compareSets = (s1, s2) => s1.size === s2.size && [...s1].every(x => s2.has(x));
        const compareArrays = (a1, a2) => a1.length === a2.length && a1.every((v, i) => v === a2[i]);
        return targetMasteryDate !== snapshot.targetMasteryDate || dailyRuleTarget !== snapshot.dailyRuleTarget || mixMode !== snapshot.mixMode ||
            !compareArrays(studyDays.sort(), [...snapshot.studyDays].sort()) || !compareArrays(subjectSequence, snapshot.subjectSequence) ||
            !compareSets(excludedSubjects, snapshot.excludedSubjects) || !compareSets(hfrRestrictions, snapshot.hfrRestrictions) ||
            !compareSets(deNovoTrackSubjects, snapshot.deNovoTrackSubjects);
    }, [snapshot, targetMasteryDate, dailyRuleTarget, mixMode, studyDays, subjectSequence, excludedSubjects, hfrRestrictions, deNovoTrackSubjects]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (!isDirty) return;
        onSaveConfiguration({
            subject_sequence: subjectSequence, excluded_subjects: Array.from(excludedSubjects),
            hfr_restricted_subjects: Array.from(hfrRestrictions), denovo_track_subjects: Array.from(deNovoTrackSubjects),
            mix_mode: mixMode, daily_rule_target: parseInt(dailyRuleTarget, 10),
            target_mastery_date: targetMasteryDate, study_days: studyDays
        });
        onClose();
    };

    const toggleSet = (setter, set, value) => {
        const newSet = new Set(set);
        if (newSet.has(value)) newSet.delete(value); else newSet.add(value);
        setter(newSet); setActiveArchetype('custom'); 
    };

    const applyArchetype = (type) => {
        setActiveArchetype(type);
        const all = Object.keys(subjectMap);
        setDeNovoTrackSubjects(new Set()); setExcludedSubjects(new Set());
        if (type === 'traditional') { setMixMode(false); setHfrRestrictions(new Set()); }
        else if (type === 'interleaved') { setMixMode(true); setHfrRestrictions(new Set()); }
        else if (type === 'high_yield') { setMixMode(true); setHfrRestrictions(new Set(all)); }
        else if (type === 'shield') { setMixMode(false); setHfrRestrictions(new Set(all.filter(id => !MBE_SUBJECTS.includes(id)))); }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="config-modal wide theme-aware" onClick={e => e.stopPropagation()}>
                <header className="config-header">
                    <div className="header-titles">
                        <h2>Strategy & Timeline</h2>
                        {isDirty && <span className="dirty-indicator">Unsaved Changes</span>}
                    </div>
                    <button className="icon-btn close-btn" onClick={onClose}>&times;</button>
                </header>
                <div className="config-body">
                    <section className="config-section">
                        <h3>1. Timeline & Availability</h3>
                        <div className="global-settings-row">
                            <div className="setting-card">
                                <label className="setting-label">Target Mastery Date</label>
                                <input type="date" value={targetMasteryDate} onChange={(e) => { setTargetMasteryDate(e.target.value); setActiveArchetype('custom'); }} />
                            </div>
                            <div className="setting-card">
                                <label className="setting-label">Weekly Study Days</label>
                                <div className="day-selector-modal">
                                    {DAYS_OF_WEEK_KEYS.map((day, i) => (
                                        <button key={day} className={studyDays.includes(day) ? 'active' : ''} onClick={() => setStudyDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}>
                                            {DAYS_OF_WEEK_LABELS[i]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="config-section">
                        <h3>2. Strategy Presets</h3>
                        <div className="archetype-selector-row">
                            {ARCHETYPES.map(arch => (
                                <button key={arch.id} className={`archetype-chip ${activeArchetype === arch.id ? 'active' : ''}`} onClick={() => applyArchetype(arch.id)}>
                                    <span className="material-symbols-outlined">{arch.icon}</span>
                                    <div className="chip-text">
                                        <span className="title">{arch.title}</span>
                                        <span className="desc">{arch.tag}</span>
                                    </div>
                                    <div className="arch-tooltip">
                                        <div className="tooltip-specs">
                                            {arch.specs.map((spec, sIdx) => (
                                                <div key={sIdx} className="spec-row">
                                                    <span className="spec-label">{spec.label}:</span>
                                                    <span className="spec-val">{spec.val}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="config-section global-settings-row">
                        <div className="setting-card tooltip-anchor">
                            <label className="setting-label"><span className="material-symbols-outlined">shuffle</span> Mix Mode</label>
                            <label className="toggle-switch"><input type="checkbox" checked={mixMode} onChange={(e) => { setMixMode(e.target.checked); setActiveArchetype('custom'); }} /><span className="slider round"></span></label>
                            <div className="arch-tooltip"><strong>Linear (Off):</strong> Study one subject at a time.<br/><strong>Mix (On):</strong> Interleave subjects daily to improve retention.</div>
                        </div>
                        <div className="setting-card tooltip-anchor">
                            <label className="setting-label"><span className="material-symbols-outlined">speed</span> Minimum Goal ({dailyRuleTarget}/Day)</label>
                            <input type="range" min="1" max="15" value={dailyRuleTarget} onChange={(e) => setDailyRuleTarget(e.target.value)} />
                            <div className="arch-tooltip">Your baseline daily target. Wali's will auto-increase this pace if you fall behind schedule.</div>
                        </div>
                    </section>

                    <section className="config-section">
                        <h3>3. Curriculum Detail</h3>
                        <div className="sortable-list">
                            {subjectSequence.map((subjectId, index) => (
                                <div key={subjectId} className={`sortable-item ${excludedSubjects.has(subjectId) ? 'inactive' : ''}`} draggable onDragStart={(e) => {setDraggedItem(index); setActiveArchetype('custom');}} onDragOver={(e) => {e.preventDefault(); if (draggedItem !== index) {const n=[...subjectSequence]; const i=n.splice(draggedItem,1)[0]; n.splice(index,0,i); setSubjectSequence(n); setDraggedItem(index);}}} style={{ borderLeft: `4px solid var(--subj-${subjectId})` }}>
                                    <div className="drag-handle"><span className="material-symbols-outlined">drag_indicator</span><span className="subject-name">{subjectMap[subjectId] || subjectId}</span></div>
                                    <div className="item-controls">
                                        <button className={`option-pill hfr ${hfrRestrictions.has(subjectId) ? 'active' : ''}`} onClick={() => toggleSet(setHfrRestrictions, hfrRestrictions, subjectId)}>HFR</button>
                                        <button className={`option-pill fast ${deNovoTrackSubjects.has(subjectId) ? 'active' : ''}`} onClick={() => toggleSet(setDeNovoTrackSubjects, deNovoTrackSubjects, subjectId)}><span className="material-symbols-outlined">fast_forward</span></button>
                                        <button className="icon-btn-text" onClick={() => toggleSet(setExcludedSubjects, excludedSubjects, subjectId)}><span className="material-symbols-outlined">{excludedSubjects.has(subjectId) ? 'visibility_off' : 'visibility'}</span></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
                
                <footer className="config-footer-complex">
                    <div className="config-summary-compact">
                        <div className="summary-col">
                            <div className="s-line"><span className="lbl">InLimine</span> <span className="val">{stats.inLimine}</span></div>
                            <div className="s-line"><span className="lbl">FastTrack</span> <span className="val">{stats.fastTrack}</span></div>
                        </div>
                        <div className="summary-col">
                            <div className="s-line tooltip-anchor">
                                <span className="lbl">HFR Priority</span> <span className="val highlight-gold">{stats.hfrCount}</span>
                                <div className="arch-tooltip">"HFR" stands for <strong>High Frequency Rules</strong>. This is the number of unlearned HFRs in your plan.</div>
                            </div>
                            <div className="s-line"><span className="lbl">Req. Pace</span> <span className="val highlight-blue">{stats.velocity}/day</span></div>
                        </div>
                    </div>
                    <div className="footer-actions">
                        <button className="btn secondary" onClick={onClose}>Cancel</button>
                        <button className="btn primary" onClick={handleSave} disabled={!isDirty}>Save Changes</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default CurriculumConfigModal;
/* --- END: src/components/CurriculumConfigModal.js --- */